import {
  FormControl,
  FormField,
  LabelActions,
  RichTextTemplateTokens,
  TextEditorContentProps,
  TextEditorReadyHandler,
} from 'platform/components';
import {Integer} from 'platform/foundation';

import {FieldValues, Path} from 'react-hook-form';

import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId, useOnMount} from 'shared/web';

import {EMAIL_TOOLBAR_OPTIONS} from '../../constants/EMAIL_TOOLBAR_OPTIONS';
import {useEmailBodyImageUpload} from '../../hooks/useEmailBodyImageUpload';

interface EmailBodyFormFieldProps<TFieldValues extends FieldValues>
  extends RequiredTestIdProps, TextEditorContentProps {
  control: FormControl<TFieldValues>;
  name: Path<TFieldValues>;
  /** What `useEmailBodyImageUpload` returns, so the wiring cannot drift between screens. */
  upload: Pick<
    ReturnType<typeof useEmailBodyImageUpload>,
    'handleUploadImage' | 'cancelPendingWaits'
  >;
  labelActions?: LabelActions;
  isRequired?: boolean;
  isDisabled?: boolean;
  minRows?: Integer | Nullish;
  onEditorReady?: TextEditorReadyHandler;
  /** Shows the known `{{ code }}` placeholders as labels; read once on mount, as in `RichText`. */
  templateTokens?: RichTextTemplateTokens;
}

/**
 * The e-mail body field, wired once. Every screen offers the same formatting, so the toolbar and the
 * upload handler live here; what the screens legitimately differ on stays a prop.
 */
export function EmailBodyFormField<TFieldValues extends FieldValues>(
  props: EmailBodyFormFieldProps<TFieldValues>
) {
  // Two screens render this field conditionally, so an upload started here is given up with it.
  useOnMount(undefined, props.upload.cancelPendingWaits);

  return (
    <FormField
      control={props.control}
      name={props.name}
      type="richText"
      label={i18n.t('general.labels.message')}
      labelActions={props.labelActions}
      toolbarOptions={EMAIL_TOOLBAR_OPTIONS}
      handleUploadImage={props.upload.handleUploadImage}
      onEditorReady={props.onEditorReady}
      templateTokens={props.templateTokens}
      editorContent={props.editorContent}
      footerContent={props.footerContent}
      selectionContent={props.selectionContent}
      editorHighlights={props.editorHighlights}
      onKeyDownCapture={props.onKeyDownCapture}
      isRequired={props.isRequired}
      isDisabled={props.isDisabled}
      isScrollable
      minRows={props.minRows ?? 7}
      data-testid={suffixTestId('emailBody', props)}
    />
  );
}
